import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  IconPhone,
  IconRobot,
  IconChartBar,
  IconStar,
  IconBriefcase,
  IconClock,
  IconBook,
  IconEyeDollar,
  IconLeaf,
  IconLighter,
  IconFlask,
  IconVideo,
  IconTicket,
  IconMessage2,
  IconCertificate,
  IconMessages,
} from "@tabler/icons-react";

const content = [
  {
    title: "Convert the call center into a multichannel ‘contact center’",
    description: "Seamlessly manage phone, chat, email, and social in one place.",
    icon: IconPhone,
  },
  {
    title: "Incorporate AI and automation",
    description: "Automate routine tasks and speed up response times with AI bots.",
    icon: IconRobot,
  },
  {
    title: "Personalized service with intelligent data",
    description: "Use advanced analytics to tailor each interaction to your client’s needs.",
    icon: IconChartBar,
  },
  {
    title: "Augmented customer experience",
    description: "Enhance interactions with real-time language processing and predictive insights.",
    icon: IconStar,
  },
  {
    title: "Industry-specific specialized services",
    description: "Offer customized solutions and knowledge tailored to each industry.",
    icon: IconBriefcase,
  },
  {
    title: "24/7 availability with hybrid human/AI support",
    description: "Provide around-the-clock assistance through AI plus on-call experts.",
    icon: IconClock,
  },
  {
    title: "Innovation in talent management",
    description: "Train and evolve your legal and support teams with continuous learning.",
    icon: IconBook,
  },
  {
    title: "Additional monetization with digital products",
    description: "Expand your portfolio by offering premium digital tools and resources.",
    icon: IconEyeDollar,
  },
  {
    title: "Sustainable and socially responsible business models",
    description: "Combine profitability with environmental and social stewardship.",
    icon: IconLeaf,
  },
  {
    title: "Constant technological innovation",
    description: "Continuously upgrade and experiment with emerging AI technologies.",
    icon: IconLighter,
  },
  {
    title: "Proactive customer experience",
    description: "Anticipate customer needs before they even ask, using smart triggers.",
    icon: IconFlask,
  },
  {
    title: "Foster an innovation culture",
    description: "Encourage experimentation and creative solutions across the organization.",
    icon: IconLighter,
  },
  {
    title: "Offer a live interactive demo",
    description: "Allow prospects to test the solution in real time to showcase true value.",
    icon: IconVideo,
  },
  {
    title: "Instant automated follow-up",
    description: "Send relevant documents and summaries immediately after each call.",
    icon: IconClock,
  },
  {
    title: "Extreme real-time personalization",
    description: "Adapt conversation flow, tone, and content on-the-fly for every client.",
    icon: IconMessages,
  },
  {
    title: "Gamified challenges",
    description: "Engage teams or clients with fun, reward-based progress tracking.",
    icon: IconStar,
  },
  {
    title: "Real-time video support",
    description: "Quickly switch to video calls for complex cases or face-to-face discussion.",
    icon: IconVideo,
  },
  {
    title: "Instant trial coupons",
    description: "Trigger trial codes during calls for immediate product or service testing.",
    icon: IconTicket,
  },
  {
    title: "Live service feedback",
    description: "Gather customer feedback in the moment to rapidly address issues.",
    icon: IconMessage2,
  },
  {
    title: "Immediate results",
    description: "Deliver on-the-spot solutions or recommendations with AI assistance.",
    icon: IconClock,
  },
  {
    title: "No commitment trial with future benefits",
    description: "Offer a test period and reward longer-term adoption with added perks.",
    icon: IconCertificate,
  },
  {
    title: "Dynamic conversation feedback",
    description: "Track sentiment and adjust the conversation direction in real time.",
    icon: IconMessages,
  },
];

// Simple Card component
const Card = ({ title, description, icon: IconUsed }) => {
  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white text-black p-6 rounded-2xl shadow-lg text-center w-full max-w-sm mx-auto"
    >
      <IconUsed size={40} className="text-blue-500 mx-auto mb-4" />
      <h3 className="font-semibold text-lg">{title}</h3>
      <p className="text-gray-500 text-sm mt-2">{description}</p>
    </motion.div>
  );
};

const About = () => {
  // Show 6 items initially (2 rows x 3 columns in md).
  const [visibleCount, setVisibleCount] = useState(6);

  const visibleItems = content.slice(0, visibleCount);

  const handleLoadMore = () => {
    // Show next 6 items at a time; if we exceed total length, clamp to full length.
    setVisibleCount((prev) => Math.min(prev + 6, content.length));
  };

  const handleShowLess = () => {
    // Go back to just the first 6
    setVisibleCount(6);
  };

  // Decide if all items are currently visible
  const allShown = visibleCount >= content.length;

  return (
    <div className="pb-30 sm:pb-30 lg:px-10 sm:px-10 px-4">
      {/* Top Section (Banner) */}
      <div className="bg-gradient-to-r from-black to-gray-700 text-white p-10 rounded-3xl h-full lg:h-96 pb-24 lg:pb-auto">
        <div className="text-center max-w-5xl mx-auto">
          <button className="px-4 py-2 border border-white rounded-full text-sm">
            ABOUT US
          </button>
          <p className="text-gray-400 text-lg my-4">
            Experience seamless call handling in legal use-cases like client
            support, intake, and follow-ups with our intelligent and efficient
            AI agent, <strong>Your Virtual Office AI</strong>.
          </p>
          <h2 className="text-5xl font-bold mt-2">
            State-of-the-Art Technology Based on <br /> Years of AI Research
          </h2>
        </div>
      </div>

      {/* Cards Section */}
      <div className="flex justify-center mt-20">
        <div className="-mt-32">
          {/* Grid: 2 columns on small screens, 3 columns on md+ */}
          <div className="w-full max-w-7xl px-4 grid grid-cols-2 md:grid-cols-3 gap-6">
            {visibleItems.map((item, index) => (
              <Card key={index} {...item} />
            ))}
          </div>

          {/* Button Section */}
          <div className="text-center mt-8">
            {!allShown ? (
              <button
                onClick={handleLoadMore}
                className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-full shadow-md hover:bg-blue-700 transition duration-300"
              >
                Load More
              </button>
            ) : (
              <button
                onClick={handleShowLess}
                className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-full shadow-md hover:bg-blue-700 transition duration-300"
              >
                Show Less
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
